/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { simpleFetch } from '../../restWrapper';
const money = (value, currency) => value == null ? '—' : new Intl.NumberFormat('en-HK', {
  style: 'currency', currency: (currency || 'HKD').toUpperCase()
}).format(value);
const StripeSync = forwardRef(function StripeSync({ payment, text, onRefresh, showSyncButton = true, compact = false, onCheckingChange }, ref) {
  const [state, setState] = useState({});
  const generation = useRef(0);
  useEffect(() => { generation.current++; setState({}); return () => { generation.current++; }; }, [payment.id]);
  useEffect(() => { if (onCheckingChange) onCheckingChange(!!state.busy, payment.id, state.verification); }, [state.busy, state.verification, payment.id, onCheckingChange]);
  const request = async apply => {
    if (state.busy || (apply && !state.preview?.canApply)) return;
    const version = generation.current;
    const confirmation = state.preview?.confirmation;
    setState(previous => ({ ...previous, busy: true, message: '', failed: false }));
    try {
      let response = await simpleFetch(`/organizations/payments/${payment.id}/sync-stripe${apply ? '/confirm' : ''}`,
        'POST', apply ? { confirmation } : {});
      if (version !== generation.current) return;
      if (!apply && response.status === 200 && response.body?.canApply &&
          Array.isArray(response.body.changes) && response.body.changes.length === 0) {
        // The server refetches Stripe and rejects a changed snapshot before
        // recording verification. Financial differences still require review.
        apply = true;
        response = await simpleFetch(`/organizations/payments/${payment.id}/sync-stripe/confirm`,
          'POST', { confirmation: response.body.confirmation });
        if (version !== generation.current) return;
      }
      if (response.status !== 200) {
        setState({ failed: true, verification: 'review', message: text[response.body?.code] || text.provider_unavailable });
      } else if (apply) {
        let refreshed;
        try { refreshed = await onRefresh(); } catch (_) { refreshed = false; }
        if (version !== generation.current) return;
        setState({ verification: refreshed !== false && !response.body.receiptStale ? 'verified' : 'review',
          verified: refreshed !== false && !response.body.receiptStale && response.body.result === 'unchanged',
          message: refreshed === false ? text.refreshFailed : response.body.receiptStale ? text.receiptReview :
            response.body.result === 'unchanged' ? text.unchanged : text.updated });
      } else setState({ preview: response.body, verification: response.body.canApply && response.body.changes?.length ? 'differences' : 'review' });
    } catch (_) {
      if (version === generation.current) setState({ failed: true, verification: 'review', message: text.provider_unavailable });
    }
  };
  useImperativeHandle(ref, () => ({ sync: () => request(false) }));
  const preview = state.preview;
  const lastCheck = payment.stripeManualCheckedAt || payment.stripeFeeConfirmedAt;
  const showAdjustment = preview && (preview.changes?.includes('stripePlatformCostAdjustment') ||
    Number(preview.stored?.stripePlatformCostAdjustment || 0) !== 0 || Number(preview.provider?.stripePlatformCostAdjustment || 0) !== 0);
  return <React.Fragment>
    {(!compact || showSyncButton || (state.busy && !onCheckingChange)) && <div className="AccountOverview-stripeToolbar">
      {showSyncButton && <button type="button" disabled={state.busy} onClick={() => request(false)}>{state.busy ? text.checking : text.sync}</button>}
      {!showSyncButton && state.busy && !onCheckingChange && <span role="status">{text.checking}</span>}
      {!compact && lastCheck && Number.isFinite(Date.parse(lastCheck)) && <p className="AccountOverview-stripeLastCheck">
        <span>{text.lastCheck}</span><time dateTime={new Date(lastCheck).toISOString()}>{new Date(lastCheck).toLocaleString()}</time>
      </p>}
    </div>}
    {preview && <div className="AccountOverview-paymePreview AccountOverview-stripePreview" role="region" aria-label={text.reviewTitle}>
      <div className="AccountOverview-stripeReviewHeading">
        <h3>{text.reviewTitle}</h3>
        {preview.canApply && preview.changes?.length > 0 && <span className="AccountOverview-stripeDifferenceCount">
          {(preview.changes.length === 1 ? text.differenceCount : text.differencesCount).replace('{count}', preview.changes.length)}
        </span>}
      </div>
      <div className={`AccountOverview-stripeReviewIntro ${preview.canApply && preview.changes?.length ? 'has-differences' : ''}`}>
        <span aria-hidden="true">{preview.canApply && preview.changes?.length ? '!' : 'i'}</span>
        <p>{preview.canApply && preview.changes?.length ? text.reviewSummary : text.readOnly}</p>
      </div>
      <div className="AccountOverview-stripeComparisonTable"><table><thead><tr><th>{text.field}</th><th>{text.stored}</th><th>{text.provider}</th></tr></thead>
        <tbody>{[['processingFee', text.processing], ['serviceFee', text.service], ['fee', text.fee], ['net', text.net],
          ...(showAdjustment ? [['stripePlatformCostAdjustment', text.adjustment]] : [])].map(([key, label]) => <tr key={key}
          className={`${preview.changes?.includes(key) ? 'is-changed' : ''} ${['fee', 'net'].includes(key) ? 'is-total' : ''}`}>
          <th scope="row">{label}{preview.changes?.includes(key) && <small className="AccountOverview-stripeChangeLabel">{text.changed}</small>}</th>
          <td>{money(preview.stored?.[key], preview.stored?.currency)}</td>
          <td className={preview.changes?.includes(key) ? 'AccountOverview-paymeChanged' : ''}>
            {money(preview.provider?.[key], preview.provider?.currency)}
            {preview.changes?.includes(key) && typeof preview.provider?.[key] === 'number' && typeof preview.stored?.[key] === 'number' &&
              <small className="AccountOverview-stripeDelta">{preview.provider[key] > preview.stored[key] ? '+' : '−'}{money(Math.abs(preview.provider[key] - preview.stored[key]), preview.provider.currency)}</small>}
          </td>
        </tr>)}</tbody>
      </table></div>
      {preview.canApply && <p className="AccountOverview-stripeChargeNote">{text.chargeUnchanged}</p>}
      {preview.problem && <p role="alert">{text[preview.problem.code] || text.provider_unavailable}</p>}
      {preview.canApply && !preview.changes?.length && <p>{text.noDifferences}</p>}
      {showAdjustment && <p className="AccountOverview-stripeHint">{text.adjustmentHelp}</p>}
      <div className="AccountOverview-stripeNotes">
        {preview.canApply && preview.hasReceipt && <p>{text.keepReceipt}</p>}
        {preview.canApply && preview.receiptMayBeIssued && <p>{text.receiptMayBeIssued}</p>}
        {preview.canApply && preview.historical && <p>{text.historicalReceipt}</p>}
      </div>
      <div className="AccountOverview-paymeActions AccountOverview-stripeActions">
        {preview.canApply && <button className="AccountOverview-paymePrimary" type="button" disabled={state.busy}
          onClick={() => request(true)}>{state.busy ? text.applying : text.confirmChanges}</button>}
        <button type="button" disabled={state.busy} onClick={() => setState({})}>{text.cancel}</button>
      </div>
    </div>}
    {state.message && !(compact && state.verified) && <p className={`AccountOverview-stripeOutcome ${state.failed ? 'is-error' : state.verified ? 'is-verified' : ''}`}
      role={state.failed ? 'alert' : 'status'}>
      {state.verified && <span className="AccountOverview-stripeVerifiedTick" aria-hidden="true">✓</span>}
      {state.message}
    </p>}
  </React.Fragment>;
});
export default StripeSync;
StripeSync.propTypes = { payment: PropTypes.object.isRequired, text: PropTypes.object.isRequired, onRefresh: PropTypes.func.isRequired, showSyncButton: PropTypes.bool, compact: PropTypes.bool, onCheckingChange: PropTypes.func };
